"use client"

import * as React from "react"
import { Link } from "react-router"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/ui/status-badge"
import { cn } from "@/lib/utils"
import { formatTermDateRange } from "@/lib/evaluation-term-utils"
import {
  evaluationAnalyticsPath,
  evaluationDistributionPath,
  evaluationReviewPath,
  evaluationSchedulePath,
  evaluationTermDetailsPath,
  evaluationTermSetupPath,
  prismCoursesPath,
} from "@/lib/evaluation-routes"
import {
  evaluationCoveragePercent,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"
import { useProduct } from "@/contexts/product-context"
import { TermEvaluationSummary } from "./term-evaluation-summary"

interface TermCardProps {
  term: EvaluationTerm
  column: "current" | "last" | "upcoming" | "future"
}

const CARD_FOOTER_CLASS =
  "mt-auto flex flex-wrap gap-2 border-t border-border/60 pt-4"

function MetricRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium tabular-nums text-foreground">{value}</span>
    </div>
  )
}

type PrimaryAction = {
  label: string
  href: string
  variant?: "default" | "outline" | "ghost"
}

function resolvePrimaryAction(term: EvaluationTerm, product: ReturnType<typeof useProduct>["product"]): PrimaryAction | null {
  const { counts } = term
  const setupGap = counts.notSetup + counts.draft

  if (setupGap > 0) {
    return {
      label: "Schedule evaluations",
      href: evaluationSchedulePath(product, { termId: term.id, stages: "notSetup,draft" }),
    }
  }
  if (counts.live > 0) {
    return {
      label: "Remind or extend",
      href: evaluationDistributionPath(product, { termId: term.id, action: "remind" }),
    }
  }
  if (counts.scheduled > 0) {
    return {
      label: "Update schedule",
      href: evaluationDistributionPath(product, { termId: term.id, action: "update" }),
      variant: "outline",
    }
  }
  if (counts.closed > 0) {
    return {
      label: "Review feedback",
      href: evaluationReviewPath(product, term.id),
      variant: "outline",
    }
  }
  return null
}

export function TermCard({ term, column }: TermCardProps) {
  const { product } = useProduct()
  const { courseOfferings } = term
  const showNeedsAttention = term.needsAttention && column === "last"
  const isPublished = term.cardCase === "all-published"

  if (term.cardCase === "no-courses") {
    return (
      <Card size="sm" className="flex h-full flex-col">
        <CardHeader className="pb-2">
          <TermCardHeader term={term} showNeedsAttention={showNeedsAttention} />
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            No courses found. Add courses to scheduling evaluations.
          </p>
        </CardContent>
        <CardFooter className={CARD_FOOTER_CLASS}>
          <Button size="sm" asChild>
            <Link to={prismCoursesPath()}>Add courses</Link>
          </Button>
        </CardFooter>
      </Card>
    )
  }

  if (term.cardCase === "no-evaluations") {
    return (
      <Card size="sm" className="flex h-full flex-col">
        <CardHeader className="pb-2">
          <TermCardHeader term={term} showNeedsAttention={showNeedsAttention} />
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {courseOfferings} course offerings ready to schedule.
          </p>
        </CardContent>
        <CardFooter className={CARD_FOOTER_CLASS}>
          <Button size="sm" asChild>
            <Link to={evaluationSchedulePath(product, { termId: term.id, stages: "notSetup,draft" })}>
              Schedule evaluations
            </Link>
          </Button>
        </CardFooter>
      </Card>
    )
  }

  const isAnalyticsTerm =
    term.cardCase === "all-closed" || term.cardCase === "all-published"
  const primaryAction = !isAnalyticsTerm ? resolvePrimaryAction(term, product) : null
  const coverage = evaluationCoveragePercent(term.counts, term.courseOfferings)

  return (
    <Card
      size="sm"
      className={cn(
        "flex h-full flex-col",
        showNeedsAttention && "ring-2 ring-chart-4/40",
      )}
    >
      <CardHeader className="pb-2">
        <TermCardHeader
          term={term}
          showNeedsAttention={showNeedsAttention}
          published={isPublished}
        />
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-4">
        {!isAnalyticsTerm ? <TermEvaluationSummary term={term} /> : null}

        {isAnalyticsTerm ? (
          <>
            <TermEvaluationSummary term={term} showStageNotes={false} />
            {term.ratings ? (
              <div className="grid grid-cols-2 gap-3">
                <MetricRow label="Course avg" value={term.ratings.courseAverage.toFixed(1)} />
                <MetricRow label="Faculty avg" value={term.ratings.facultyAverage.toFixed(1)} />
                <MetricRow label="Program avg" value={term.ratings.programAverage.toFixed(1)} />
                {term.ratings.otherAverage != null ? (
                  <MetricRow label="Other avg" value={term.ratings.otherAverage.toFixed(1)} />
                ) : null}
                <MetricRow
                  label="Response rate"
                  value={term.closedResponseRate != null ? `${term.closedResponseRate}%` : "None"}
                />
                <MetricRow label="Coverage" value={`${coverage}%`} />
              </div>
            ) : null}
          </>
        ) : null}

        {isAnalyticsTerm && term.attention ? (
          <div className="rounded-lg border border-chart-4/30 bg-chart-4/5 px-3 py-2.5 text-xs text-foreground">
            {term.attention.coursesBelowThreshold} courses and{" "}
            {term.attention.facultyRolesBelowThreshold} faculty roles below threshold.
          </div>
        ) : null}
      </CardContent>

      <CardFooter className={CARD_FOOTER_CLASS}>
        {isAnalyticsTerm ? (
          <Button size="sm" asChild>
            <Link to={evaluationAnalyticsPath(product, term.id)}>View analytics</Link>
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" asChild>
              <Link to={evaluationTermDetailsPath(product, term.id)}>View details</Link>
            </Button>
            {primaryAction ? (
              <Button size="sm" variant={primaryAction.variant ?? "default"} asChild>
                <Link to={primaryAction.href}>{primaryAction.label}</Link>
              </Button>
            ) : null}
          </>
        )}
      </CardFooter>
    </Card>
  )
}

function TermCardHeader({
  term,
  showNeedsAttention,
  published,
}: {
  term: EvaluationTerm
  showNeedsAttention?: boolean
  published?: boolean
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {term.programName}
          </p>
          <h3 className="font-heading text-base font-semibold leading-snug text-foreground">
            {term.academicYear} · {term.termName}
          </h3>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {published ? (
            <StatusBadge
              label="Published"
              tone="success"
              icon="fa-circle-check"
              size="md"
            />
          ) : null}
          {showNeedsAttention ? (
            <StatusBadge
              label="Needs attention"
              tone="warning"
              icon="fa-triangle-exclamation"
              size="md"
            />
          ) : null}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {formatTermDateRange(term.startDate, term.endDate)}
        {term.courseOfferings > 0 ? ` · ${term.courseOfferings} courses` : null}
      </p>
    </div>
  )
}

/** Empty state when no terms are configured (Case 1 — Riverside DPT) */
export function NoTermsCard({ programName = "Riverside DPT" }: { programName?: string }) {
  const { product } = useProduct()

  return (
    <Card size="sm" className="border-dashed">
      <CardHeader>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {programName}
        </p>
        <h3 className="font-heading text-base font-semibold text-foreground">
          Set up your first term
        </h3>
        <p className="text-sm text-muted-foreground">
          Add academic terms with start and end dates to begin scheduling course evaluations.
        </p>
      </CardHeader>
      <CardFooter className={CARD_FOOTER_CLASS}>
        <Button size="sm" asChild>
          <Link to={evaluationTermSetupPath(product)}>Set up term</Link>
        </Button>
      </CardFooter>
    </Card>
  )
}
